import {useState} from 'react';
import {I18n} from 'aws-amplify/utils';
import {
    Button,
    Drawer,
    DrawerProps,
    Group,
    ScrollArea,
    Stack,
    Text,
} from '@mantine/core';

import {RecordForm} from '../forms/RecordForm';
import {ConnectFormValues} from '../forms/types';
import {useConnectProviderMutation} from '../hooks/useProviderConnectionsQuery';
import {
    buildConnectInput,
    ConnectProviderCredential,
    isAwaitingTrustPolicy,
    ProviderConnection,
} from '../types';
import {ExternalIdPanel} from '../components/ExternalIdPanel';
import {CapabilitySnapshot} from '../components/CapabilitySnapshot';
import {presentStatus} from '../statusPresentation';
import {
    showErrorNotifications,
    showSuccessNotification,
} from '../../../components/ServiceNotifications';

// FNP-1248 — THE CONNECT DRAWER, AND THE TWO-PHASE FLOW IT IMPLEMENTS.
//
// ⚠️ THIS DRAWER DOES NOT CLOSE ON A SUCCESSFUL SUBMIT, AND THAT IS THE WHOLE
// DESIGN.
//
// The drawer pattern's `handleFormSubmit` submits then calls `onClose()`. That
// is right for an entity whose creation is complete when the mutation resolves.
// A provider connection's is NOT: for ASSUMED_ROLE, the mutation resolving is
// the moment Flexi FIRST HAS an external id to give the tenant, and closing the
// drawer at that instant would throw away the one value the whole flow exists
// to deliver — leaving a tenant with an INVALID connection, no explanation, and
// no idea what to do next.
//
// So the drawer swaps from FORM to RESULT in place:
//
//   phase 'form'   → the connect form.
//   phase 'result' → the created connection, its status explained honestly, and
//                    the external id prominently and copyably displayed.
//
// The tenant closes it themselves, once they have the id.
//
// ⚠️ A NON-ACTIVE RESULT IS NOT AN ERROR HERE. `connectProvider` stores the
// connection whatever the adapter said, so a first Route 53 connect resolves
// with INVALID/UNREACHABLE and a populated external id. That is the designed
// path. Only a REJECTED mutation (the call itself failing) keeps the form open.

type Phase =
    | { kind: 'form' }
    | { kind: 'result', connection: ProviderConnection, roleArn?: string };

export const ConnectProviderDrawer = (
    {
        opened,
        onClose,
        ...rest
    }: DrawerProps,
) => {

    const [phase, setPhase] = useState<Phase>({kind: 'form'});

    const connectMutation = useConnectProviderMutation();

    const handleClose = () => {
        // Reset for the next open, so a previously created connection's
        // external id is not showing when the drawer is opened to make another.
        setPhase({kind: 'form'});
        connectMutation.reset();
        onClose();
    };

    const handleSubmit = async (values: ConnectFormValues) => {

        // ⚠️ THE CREDENTIAL IS ASSEMBLED HERE AND NOWHERE ELSE, and exactly ONE
        // field is populated — chosen by the provider's declared type, never by
        // which input happens to be non-empty. Sending both would hand a token
        // to a provider that wanted a role.
        const credential: ConnectProviderCredential =
            values.credentialType === 'ASSUMED_ROLE'
                ? {roleArn: values.roleArn.trim()}
                : {apiToken: values.apiToken};

        try {
            const connection = await connectMutation.mutateAsync(
                // FNP-1233: `writable` is omitted unless explicitly true — see
                // buildConnectInput for why omission is the correct expression
                // of "not writable" while the schema change is in flight.
                buildConnectInput(
                    values.providerId,
                    values.displayName.trim(),
                    credential,
                    values.writable,
                ),
            );

            if (!connection) {
                // The mutation resolved with no connection — nothing was
                // created and there is nothing to show. Keep the form open.
                showErrorNotifications(new Error(I18n.get('The provider connection was not created.')));
                return;
            }

            // ⚠️ THE NOTIFICATION IS HONEST ABOUT WHICH OUTCOME THIS IS. A flat
            // "Connection created" next to an INVALID badge reads as a system
            // contradicting itself.
            if (isAwaitingTrustPolicy(connection)) {
                showSuccessNotification(I18n.get('Connection created. Add its external ID to your role\'s trust policy to finish.'));
            } else if (connection.status === 'ACTIVE') {
                showSuccessNotification(I18n.get('Connected. Flexi can reach this provider.'));
            } else {
                showSuccessNotification(I18n.get('Connection saved. See its status below.'));
            }

            setPhase({
                kind: 'result',
                connection,
                // ⚠️ ONLY the role ARN is carried into the result view. It is an
                // identifier the tenant needs alongside the external id. The
                // token is NOT carried — it is not needed, must never be
                // rendered, and is dropped with the form state.
                roleArn: values.credentialType === 'ASSUMED_ROLE'
                    ? values.roleArn.trim()
                    : undefined,
            });

        } catch (e) {
            // The CALL failed — not the credential. The form stays open with the
            // tenant's input intact so they can correct and retry.
            showErrorNotifications(e);
            // Re-thrown so the Formik submission settles as failed.
            throw e;
        }
    };

    return (
        <Drawer
            transitionProps={{
                transition: 'slide-up',
                duration: 750,
                timingFunction: 'ease',
            }}
            position={'right'}
            offset={10}
            opened={opened}
            onClose={handleClose}
            title={phase.kind === 'form'
                ? I18n.get('Connect DNS Provider')
                : I18n.get('Connection created')}
            padding={'xl'}
            size={'xl'}
            scrollAreaComponent={ScrollArea.Autosize}
            {...rest}
        >
            {phase.kind === 'form' ? (
                <RecordForm
                    handleSubmit={handleSubmit}
                    handleCancel={handleClose}
                />
            ) : (
                <Stack gap={'lg'}>

                    <Stack gap={4}>
                        <Text size={'sm'} fw={500}>{phase.connection.displayName}</Text>
                        <Text size={'sm'} c={'dimmed'}>
                            {presentStatus(phase.connection).summary}
                        </Text>
                    </Stack>

                    {/* THE EXTERNAL ID. The reason this drawer stays open. */}
                    <ExternalIdPanel
                        connection={phase.connection}
                        roleArn={phase.roleArn}
                    />

                    <CapabilitySnapshot connection={phase.connection}/>

                    <Group justify={'flex-end'}>
                        <Button onClick={handleClose}>
                            {I18n.get('Done')}
                        </Button>
                    </Group>

                </Stack>
            )}
        </Drawer>
    );
};
